import { useState } from "react";
import { Car, CheckCircle2, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const field = "w-full px-4 py-3 rounded-2xl border-2 border-stone-200 bg-white text-base outline-none focus:border-brand-red";

/** "I'm here" curbside check-in on the order tracking page. */
export function CurbsideCard({ order, onUpdated }) {
  const curb = order.curbside;
  const [open, setOpen] = useState(false);
  const [vehicle, setVehicle] = useState(curb?.vehicle || "");
  const [spot, setSpot] = useState(curb?.spot || "");
  const [busy, setBusy] = useState(false);

  if (["Completed", "Cancelled"].includes(order.status)) return null;

  const submit = async (e) => {
    e.preventDefault();
    const t = localStorage.getItem(`bk_track_${order.order_number}`) || order.track_token || "";
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/track/${order.order_number}/curbside`, { t, vehicle, spot });
      toast.success(checkedIn ? "Updated — we'll find you" : "Got it — we'll bring it out to you!");
      onUpdated?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail === "verification_required" ? "Please reopen this page from your confirmation link first." : err?.response?.data?.detail || "Couldn't check in — try again");
    } finally { setBusy(false); }
  };

  const checkedIn = !!curb?.checked_in_at;
  if (curb && !checkedIn && !open) {
    return (
      <section className="rounded-3xl border-2 border-brand-red bg-white p-5" data-testid="curbside-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-stone-900 text-brand-yellow flex items-center justify-center shrink-0"><Car className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-stone-900 text-sm uppercase tracking-widest" data-testid="curbside-status">Curbside pickup</div>
            <div className="text-xs text-stone-500 mt-0.5">{curb.vehicle ? `${curb.vehicle} · ` : ""}When you pull up, tap the button so we can bring it out.</div>
          </div>
        </div>
        <button type="button" onClick={() => setOpen(true)} data-testid="curbside-checkin-btn" className="mt-3 w-full py-3 rounded-full bg-brand-red text-white text-xs uppercase tracking-widest font-black flex items-center justify-center gap-2"><MapPin className="w-4 h-4" /> I'm here</button>
      </section>
    );
  }

  if (curb && !open) {
    const delivered = !!curb.delivered_at;
    return (
      <section className={`rounded-3xl border-2 p-5 ${delivered ? "border-emerald-300 bg-emerald-50" : "border-brand-yellow bg-yellow-50"}`} data-testid="curbside-card">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${delivered ? "bg-emerald-600 text-white" : "bg-brand-yellow text-stone-900"}`}>
            {delivered ? <CheckCircle2 className="w-6 h-6" /> : <Car className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-stone-900 text-sm uppercase tracking-widest" data-testid="curbside-status">{delivered ? "Order brought out" : "You're checked in for curbside"}</div>
            <div className="text-sm text-stone-700 mt-1" data-testid="curbside-vehicle">{curb.vehicle}{curb.spot ? ` · ${curb.spot}` : ""}</div>
            {!delivered && <div className="text-xs text-stone-500 mt-1">Stay in your car — we'll walk it out as soon as it's ready.</div>}
          </div>
          {!delivered && (
            <button type="button" onClick={() => setOpen(true)} data-testid="curbside-edit-btn" className="text-stone-500 hover:text-brand-red p-1" aria-label="Update car details"><Pencil className="w-4 h-4" /></button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-3xl border-2 border-stone-200 bg-white p-5" data-testid="curbside-card">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="curbside-checkin-btn"
          className="w-full flex items-center gap-3 text-left">
          <div className="w-11 h-11 rounded-2xl bg-stone-900 text-brand-yellow flex items-center justify-center shrink-0"><Car className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="font-black text-stone-900 text-sm uppercase tracking-widest">Here for curbside?</div>
            <div className="text-xs text-stone-500 mt-0.5">Tap to check in and tell us what your car looks like — we'll bring it out.</div>
          </div>
          <MapPin className="w-5 h-5 text-brand-red shrink-0" />
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="curbside-form">
          <div className="font-black text-stone-900 text-sm uppercase tracking-widest flex items-center gap-2"><Car className="w-4 h-4 text-brand-red" /> Curbside check-in</div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">What does your car look like?</span>
            <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. Blue Honda Civic" className={field} data-testid="curbside-vehicle-input" autoFocus required maxLength={80} />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Where are you parked? (optional)</span>
            <input value={spot} onChange={(e) => setSpot(e.target.value)} placeholder="e.g. by the mailbox, spot 4" className={field} data-testid="curbside-spot-input" maxLength={80} />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full border-2 border-stone-200 text-xs uppercase tracking-widest font-black text-stone-600" data-testid="curbside-cancel-btn">Cancel</button>
            <button type="submit" disabled={busy || vehicle.trim().length < 3} data-testid="curbside-submit-btn"
              className="flex-[2] py-3 rounded-full bg-brand-red text-white text-xs uppercase tracking-widest font-black disabled:opacity-40">{busy ? "Checking in…" : checkedIn ? "Update" : "I'm here"}</button>
          </div>
        </form>
      )}
    </section>
  );
}
